/**
 * The profile's identity header (`04-officials-profiles.md` §B).
 *
 * Six read-only cells in the `.markhead` idiom the marking screen's TMS strip already uses,
 * so the two screens about one official read the same way: who they are, what they are here
 * as, what TMS counts them at, how far their report has got, and what the marking currently
 * comes to.
 *
 * **The same projections the roster uses.** `mediaName`, `country`, `role`, `gender` and the
 * cap-with-its-unit are the roster's own columns, and the unit rule is imported from the one
 * module that states it rather than restated here — §A3's *"one shared derived value, not two
 * copies"*, applied to the cap as it already is to the report stage.
 *
 * **Nothing is rendered before it is known.** A cell whose fetch is still in flight shows the
 * honest em dash with the reason on hover, never a zero — §B9's *"never render a 0.0% stat
 * box while loading"*, which §B14 restates for this page as *"never let Report Readiness
 * render 0% while loading — it reads as a real finding"*.
 *
 * **The TD's own profile has no reporting cells at all** (ruling R2′): he receives no report
 * and no marks, so the two cells are absent rather than empty.
 */

import type { ReportStage } from "@hockeytd/shared";

import type { OfficialDetailView } from "../../api/types.js";
import { CountryFlag } from "../../app/CountryFlag.js";
import { bandFor } from "../marking/outcome.js";
import { CAP_UNIT_SHORT, capTooltip } from "../reporting/capUnit.js";
import { OwnedByUmMarker, ReportStatusPill } from "../reporting/ReportStatusPill.js";
import { OfficialAvatar } from "./OfficialAvatar.js";

export interface ProfileHeaderProps {
  official: OfficialDetailView;
  /** False on the TD, whose profile carries no Reporting Section at all (ruling R2′). */
  reportable: boolean;
  /** The stage, or null while the report read is still in flight or has refused. */
  stage: ReportStage | null;
  ownedByUm: boolean;
  ownerUmShortName: string | null;
  /** The stored Performance Outcome, null when unmarked, undefined while not yet read. */
  outcome: number | null | undefined;
}

/** The `—` an unknown cell shows, with the reason on hover rather than in the layout. */
function Unknown({ reason }: { reason: string }): React.JSX.Element {
  return (
    <span className="unmarked" title={reason}>
      —
    </span>
  );
}

export function ProfileHeader({
  official,
  reportable,
  stage,
  ownedByUm,
  ownerUmShortName,
  outcome,
}: ProfileHeaderProps): React.JSX.Element {
  const band = outcome === undefined ? null : bandFor(outcome);

  return (
    <div className="markhead" data-testid="profile-header">
      <div>
        <span className="k">Official</span>
        <span className="v">
          {/*
            Run 29 — the face here too, at the inline size this cell has always drawn. Run 27
            left it as initials to avoid a second fetch of a photograph the card below was
            already fetching; the session cache in `useOfficialPhoto` means the two now share
            one request, so the objection has gone and the two avatars about one person on one
            screen no longer disagree about what he looks like.
          */}
          <OfficialAvatar
            tmsPersonId={official.tmsPersonId}
            mediaName={official.mediaName}
            gender={official.gender}
            photoRef={official.photoRef}
          />{" "}
          {official.mediaName}
        </span>
      </div>
      <div>
        <span className="k">Country</span>
        <span className="v num">
          <CountryFlag code={official.country} />
        </span>
      </div>
      <div>
        <span className="k">Role For This Tournament</span>
        <span className="v">{official.role}</span>
      </div>
      <div>
        <span className="k">Cap</span>
        <span className="v num" data-testid="profile-cap" title={capTooltip(official.role)}>
          {official.cap === null ? (
            <Unknown reason="No cap recorded on the roster record" />
          ) : (
            <>
              {official.cap}
              {official.capUnit === null ? null : (
                <span className="of"> {CAP_UNIT_SHORT[official.capUnit]}</span>
              )}
            </>
          )}
        </span>
      </div>

      {reportable ? (
        <>
          <div>
            <span className="k">Report Status</span>
            <span className="v" data-testid="profile-report-stage">
              {stage === null ? (
                <Unknown reason="The report has not been read yet" />
              ) : (
                <>
                  <ReportStatusPill stage={stage} />
                  {ownedByUm ? <OwnedByUmMarker ownerUmShortName={ownerUmShortName} /> : null}
                </>
              )}
            </span>
          </div>
          <div>
            <span className="k">Performance Outcome</span>
            <span className="v big">
              <span data-testid="performance-outcome">
                {outcome === undefined ? (
                  <Unknown reason="The marking has not been read yet" />
                ) : outcome === null ? (
                  <span className="unmarked">—</span>
                ) : (
                  outcome
                )}
              </span>
              {outcome === undefined ? null : (
                <span
                  className={`outcome-band ${band === null ? "band--none" : band.className}`}
                  data-testid="performance-band"
                >
                  {band === null ? "Not Marked" : band.label}
                </span>
              )}
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}
